import { useCallback, useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import {
  PCMP_ACCOUNTS_DEFAULT_TAB,
  pcmpAccountsViewSource,
} from '../constants/pcmpAccounts.constants';
import type {
  PcmpAccountsBatchView,
  PcmpAccountsPageParams,
  PcmpAccountsQueueSearch,
  PcmpAccountsView,
} from '../types/pcmpAccounts.types';
import { usePcmpQueueFilters } from './usePcmpQueueFilters';

const route = getRouteApi('/_auth/pcmp/accounts/');

export function usePcmpAccountsFilters() {
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const setSearch = useCallback(
    (update: (prev: PcmpAccountsQueueSearch) => PcmpAccountsQueueSearch) => {
      void navigate({ search: update });
    },
    [navigate],
  );

  const queue = usePcmpQueueFilters(search, setSearch);
  const { appliedFilters, page, limit } = queue;

  const tab = search.tab ?? PCMP_ACCOUNTS_DEFAULT_TAB;
  const urgentView: PcmpAccountsBatchView = search.urgentView ?? 'PENDING';
  const source = useMemo(() => pcmpAccountsViewSource(tab, urgentView), [tab, urgentView]);

  /** The filters and paging every tab's list takes. */
  const pageParams = useMemo<PcmpAccountsPageParams>(
    () => ({ ...appliedFilters, page, limit }),
    [appliedFilters, page, limit],
  );

  const setTab = useCallback(
    (next: PcmpAccountsView) => {
      setSearch((prev) => ({
        ...prev,
        tab: next === PCMP_ACCOUNTS_DEFAULT_TAB ? undefined : next,
        urgentView: undefined,
        page: undefined,
      }));
    },
    [setSearch],
  );

  const setUrgentView = useCallback(
    (next: PcmpAccountsBatchView) => {
      setSearch((prev) => ({
        ...prev,
        urgentView: next === 'PENDING' ? undefined : next,
        page: undefined,
      }));
    },
    [setSearch],
  );

  return { ...queue, tab, urgentView, source, pageParams, setTab, setUrgentView };
}

export type PcmpAccountsFilterController = ReturnType<typeof usePcmpAccountsFilters>;
